import { matchPath } from 'react-router-dom';

import { VIEW_BLOG_DATE_LEGACY, namedRoutes } from '../page-common/routes';

type ParsedUrlProps = {
	contentId: string;
	contentSlug?: string;
	draftShareId?: string;
	hash?: string;
	hostname?: string;
	parentProduct: string;
	protocol: string;
	query: { [key: string]: string | string[] | undefined };
	route: string;
	spaceKey?: string;
	themeState?: string;
};

type UrlMatch = {
	hash: string;
	host: string;
	params: { [key: string]: string };
	pathname: string;
	pattern: string;
	protocol: string;
	query: { [key: string]: string | string[] | undefined };
	search: string;
	themeState?: string;
	url: string;
};

function findAndMatchRoute(
	routes: typeof namedRoutes,
	url: string,
): ({ routeName: string } & UrlMatch) | null {
	for (const routeKey of Object.keys(routes)) {
		const route = routes[routeKey];
		const match = matchUrl(route.pattern, url);
		if (match) {
			return { routeName: route.name, ...match };
		}
	}
	return null;
}

function isValidURL(url: string): boolean {
	try {
		new URL(url, window.location.origin);
		return true;
	} catch {
		return false;
	}
}

function matchUrl(pattern: string, url: string): UrlMatch | null {
	if (!isValidURL(url)) {
		return null;
	}

	const parsed = new URL(url, window.location.origin);

	const pathname: string = parsed.pathname || '';
	const search: string = parsed.search || '';

	let match = matchPath({ path: pattern, end: true }, pathname);

	// If initial match fails and the route requires query parameters, attempt to match including search
	// some of our routes need query params to match
	if (!match && search) {
		match = matchPath({ path: pattern, end: true }, `${pathname}${search}`);
	}

	if (!match) {
		return null;
	}

	// URI decode params
	const decodedParams: { [key: string]: string } = {};
	if (match.params) {
		for (const [key, value] of Object.entries(match.params)) {
			if (value !== undefined) {
				decodedParams[key] = value;
			}
		}
	}

	return {
		url,
		protocol: parsed.protocol,
		host: parsed.host,
		pathname,
		search: parsed.search,
		hash: parsed.hash,
		pattern,
		params: decodedParams,
		query: Object.fromEntries(parsed.searchParams.entries()),
	};
}

/**
 * @deprecated This is a temporary API function as ViewPage and EditPage components will
 * support url prop soon. See CBT-954 for more information
 */
export function parseUrl(url: string | undefined): ParsedUrlProps | null {
	if (!url) {
		return null;
	}

	const matchedRoute = findAndMatchRoute(namedRoutes, url);

	if (!matchedRoute) {
		return null;
	}

	const { routeName, ...match } = matchedRoute;

	const { year, month, day, contentId } = match.params;

	// Validate date parameters
	const isValidYear = /^\d+$/.test(year || '');
	const isValidMonth = /^\d+$/.test(month || '');
	const isValidDay = /^\d+$/.test(day || '');
	const isValidContentId = /^\d+$/.test(contentId || '');

	// If route requires validation, and any parameter is invalid, return null
	if (
		routeName === VIEW_BLOG_DATE_LEGACY.name &&
		(!isValidYear || !isValidMonth || !isValidDay || !isValidContentId)
	) {
		return null;
	}

	return {
		route: routeName,
		hostname: match.host,
		protocol: match.protocol,
		contentId: contentId || '',
		spaceKey: match.params.spaceKey || '',
		hash: match.hash || '',
		// EP currently supports only 1 parent product, no more than 1 parent product should be passed in
		parentProduct: Array.isArray(match.query.parentProduct)
			? match.query.parentProduct[0]
			: match.query.parentProduct || '',
		draftShareId: Array.isArray(match.query.draftShareId)
			? match.query.draftShareId[0]
			: match.query.draftShareId,
		themeState: match.query.themeState as string,
		contentSlug: match.params.contentSlug || undefined,
		query: match.query,
	};
}
